(ns renolit-roofing-service.layout.form.form-item.advanced-select
  (:require
   ["react" :as react]
   ["@floating-ui/react" :as floating-ui]
   [goog.object :as g]
   [medley.core :as medley]
   [re-frame.core :as rf]
   [renolit-roofing-service.layout.advanced-tooltip :as layout.advanced-tooltip]
   [renolit-roofing-service.layout.debouncer :as layout.debouncer]
   [renolit-roofing-service.layout.form.error :as form.error]
   [renolit-roofing-service.layout.form.form-item :as form.form-item]
   [renolit-roofing-service.layout.icon :as layout.icon]
   [renolit-roofing-service.layout.infinite-scroll :as layout.infinite-scroll]
   [renolit-roofing-service.localization :refer [t]]
   [renolit-roofing-service.util :as util]))

(def ^:const default-choice-id-kw :id)
(def ^:const default-choice-display-name-kw :name)

(def ^:once search-bar-debouncer
  (layout.debouncer/debouncer
   (partial util/execute-callback)
   layout.debouncer/default-debounce-interval))

(defn- get-choice-id
  [choice
   {:keys [choice-id-kw] :as _opts
    :or {choice-id-kw default-choice-id-kw}}]
  (get choice choice-id-kw))

(defn- get-choice-display-name
  [choice
   {:keys [choice-display-name-kw choice-display-name-fn] :as _opts
    :or {choice-display-name-kw default-choice-display-name-kw}}]
  (if choice-display-name-fn
    (choice-display-name-fn choice)
    (get choice choice-display-name-kw)))

(defn- handle-choice-multi-select
  [choice {:keys [state path attribute-path on-change] :as opts}]
  (let [choice-id (get-choice-id choice opts)
        {:keys [prevent-default?] :as callback} on-change
        old-choice-ids (get-in @state path)
        new-choice-ids (vec (conj old-choice-ids choice-id))]
    (when-not (get (set old-choice-ids) choice-id)
      (when-not prevent-default?
        (swap! state assoc-in path new-choice-ids)
        (when attribute-path
          (let [old-choices (get-in @state attribute-path)
                new-choices (vec (conj old-choices choice))]
            (swap! state assoc-in attribute-path new-choices))))
      (util/execute-callback callback choice-id new-choice-ids))))

(defn- handle-choice-single-select
  [choice {:keys [state path attribute-path on-change open-close-fn
                  prevent-close?] :as opts}]
  (let [choice-id (get-choice-id choice opts)
        {:keys [prevent-default?] :as callback} on-change]
    (when-not prevent-close?
      (open-close-fn false))
    (when-not prevent-default?
      (form.form-item/set-value-in-state state path choice-id)
      (when attribute-path
        (swap! state assoc-in attribute-path choice)))
    (util/execute-callback callback choice-id choice)))

(defn- handle-choice-select
  [choice opts]
  (if (:multi? opts)
    (handle-choice-multi-select choice opts)
    (handle-choice-single-select choice opts)))

(defn- handle-choice-multi-unselect
  [choice {:keys [state path attribute-path open-close-fn on-change] :as opts}]
  (let [choice-id (get-choice-id choice opts)
        {:keys [prevent-default?] :as callback} on-change
        old-choice-ids (get-in @state path)
        new-choice-ids (vec (remove #{choice-id} old-choice-ids))]
    (open-close-fn false)
    (when-not prevent-default?
      (swap! state assoc-in path new-choice-ids)
      (when attribute-path
        (let [old-choices (get-in @state attribute-path)
              new-choices (vec (remove #{choice} old-choices))]
          (swap! state assoc-in attribute-path new-choices))))
    (util/execute-callback callback choice-id new-choice-ids)))

(defn- handle-choice-unselect
  [choice opts]
  (if (:multi? opts)
    (handle-choice-multi-unselect choice opts)
    (handle-choice-single-select nil opts)))

(defn- form-item-label
  [{:keys [label label-transl-kw css-class-prefix mandatory?]}]
  [:label.form-item__label
   (cond-> {}
     css-class-prefix
     (assoc :class (str css-class-prefix "__label")))
   (str (if label-transl-kw
          (t [label-transl-kw])
          label))
   (when mandatory?
     [:span.form-item__mandatory "*"])])

(defn- get-form-item-value-single-display-value
  [{:keys [state path display-value choices
           placeholder-transl-kw attribute-path] :as opts}]
  (or
   display-value

   (and attribute-path
        (let [selected-choice-id (get-in @state path)
              choice (get-in @state attribute-path)]
          (when (= selected-choice-id (get-choice-id choice opts))
            (get-choice-display-name choice opts))))

   (and (seq choices)
        (let [selected-choice-id (get-in @state path)
              choice (medley/find-first #(= (get-choice-id % opts) selected-choice-id) choices)]
          (get-choice-display-name choice opts)))

   (and placeholder-transl-kw
        (t [placeholder-transl-kw]))))

(defn- form-item-value-single
  [opts]
  [:button.flat-btn.advanced-select-value-single
   [:span (get-form-item-value-single-display-value opts)]
   [layout.icon/icon
    {:key :expand-more
     :class "advanced-select-value-single__icon"}]])

(defn- get-form-item-value-multiple-display-value
  [{:keys [state path attribute-path
           choice-id-kw choices]
    :or {choice-id-kw default-choice-id-kw}}]
  (if attribute-path
    (mapv
     (fn [id]
       (some #(when (= id (get % choice-id-kw)) %)
             (get-in @state attribute-path)))
     (get-in @state path))
    (filter
     (fn [choice]
       (some
        #(= (get choice choice-id-kw) %)
        (get-in @state path)))
     choices)))

(defn- form-item-value-multiple
  [{:keys [disabled? read-only?] :as opts}]
  (let [choices (get-form-item-value-multiple-display-value opts)]
    [:div.advanced-select-value-multiple
     [:div.advanced-select-value-multiple__choices
      (doall
       (for [choice choices
             :let [choice-id (get-choice-id choice opts)
                   choice-display-name (get-choice-display-name choice opts)]]
         ^{:key choice-id}
         [:div.advanced-select-value-multiple__choice
          (when-not read-only?
            {:on-click #(.stopPropagation %)})
          [:span choice-display-name]
          (when-not (or disabled?
                        read-only?)
            [:button.flat-btn.advanced-select-value-multiple__choice-btn
             {:on-click #(handle-choice-unselect choice opts)}
             [layout.icon/icon
              {:key :close}]])]))]
     (when-not read-only? [:span (t [:common/add-more])])]))

(defn- form-item-value
  [{:keys [multi?] :as opts}]
  (if multi?
    [form-item-value-multiple opts]
    [form-item-value-single opts]))

(defn- form-item-dropdown
  [{:keys [state path choices search? optional?
           pagination on-page-change
           choice-placeholder choice-placeholder-kw] :as opts
    :or {choice-placeholder-kw :form.advanced-select/reset-filter}}]
  (let [state-value (get-in @state path)
        selected-choice-ids (if (sequential? state-value)
                              state-value
                              [state-value])
        unselected-choices (remove
                            #(some #{(get-choice-id % opts)} selected-choice-ids)
                            choices)]
    [:div.advanced-select-dropdown
     (when search?
       [:div.advanced-select-dropdown__search-bar
        [:button.flat-btn.advanced-select-dropdown__search-btn
         {:type "button"}
         [layout.icon/icon
          {:key :search}]]
        [:input.advanced-select-dropdown__search-input
         {:auto-focus true
          :on-change
          (fn [e]
            (let [event-value (g/getValueByKeys e "target" "value")]
              (search-bar-debouncer (:on-search opts) event-value)))}]])
     [:div.advanced-select-dropdown__choices
      (when optional?
        [:button.flat-btn.advanced-select-dropdown__choice.advanced-select-dropdown__choice--empty
         {:type "button"
          :on-click #(handle-choice-unselect nil opts)}
         (when (or choice-placeholder-kw choice-placeholder)
           (or (t [choice-placeholder-kw]) choice-placeholder))])
      (for [choice unselected-choices
            :let [choice-id (get-choice-id choice opts)
                  choice-display-name (get-choice-display-name choice opts)]]
        ^{:key (str "advanced-select-dropdown-choice__" choice-id)}
        [:button.flat-btn.advanced-select-dropdown__choice
         {:type "button"
          :on-click #(handle-choice-select choice opts)}
         choice-display-name])
      (when (and pagination on-page-change)
        [layout.infinite-scroll/main pagination on-page-change])]]))

(defn- build-open-close-fn
  [opts is-open set-open]
  (fn [state e]
    (when e
      (.preventDefault e))
    (when (not= state is-open)
      (let [callback-kw (if state :on-open :on-close)
            callback (get opts callback-kw)]
        (util/execute-callback callback)
        (set-open state)))))

(defn- set-dropdown-size
  [state]
  (let [{:keys [rects elements availableWidth]} (js->clj state :keywordize-keys true)
        reference-width (get-in rects [:reference :width])
        floating-style (g/getValueByKeys (:floating elements) "style")
        style-changes {:min-width (str reference-width "px")
                       :max-width (str availableWidth "px")}]
    (js/Object.assign floating-style (clj->js style-changes))))

(defn- default-dropdown
  [opts]
  [form-item-dropdown opts])

(defn- advanced-select
  [{:keys [context path dropdown-component]}]
  (let [component-id (str (random-uuid))
        form-errors (rf/subscribe [::form.error/errors context path])]
    (fn [{:keys [disabled? css-class-prefix full-line?] :as opts}]
      (let [[is-open set-open] (react/useState false)
            open-close-fn (build-open-close-fn opts is-open set-open)
            opts (assoc opts
                        :open-close-fn open-close-fn
                        :component-id component-id)
            use-floating (layout.advanced-tooltip/use-floating
                          {:whileElementsMounted floating-ui/autoUpdate
                           :placement "bottom-start"
                           :open is-open
                           :onOpenChange open-close-fn
                           :middleware [(floating-ui/offset 2)
                                        (floating-ui/flip)
                                        (floating-ui/size
                                         (clj->js {:apply set-dropdown-size}))]})
            use-interactions (layout.advanced-tooltip/use-interactions
                              (:context use-floating)
                              {:dismiss {:ancestorScroll true}
                               :click {:enabled (not disabled?)
                                       :toggle true}})]
        [:div.form-item.advanced-select
         (merge
          (when-let [select-props (:select-props opts)] select-props)
          {:class [(when disabled? "form-item--disabled")
                   (when (seq @form-errors) "form-item--error")
                   (when full-line? "form-item--full-line")
                   css-class-prefix]})
         (when (or (:label opts) (:label-transl-kw opts))
           [form-item-label opts])
         [:div.advanced-select__reference
          (assoc (:reference-props use-interactions)
                 :ref #(.setReference (:refs use-floating) %))
          [form-item-value opts]]
         (when is-open
           [:> floating-ui/FloatingPortal
            {:id "app-container"}
            [:> floating-ui/FloatingFocusManager
             {:initialFocus -1
              :context (:context use-floating)}
             (let [dropdown-component (or dropdown-component
                                          default-dropdown)
                   dropdown-props (:dropdown-props opts)]
               [:div.advanced-select__floating
                (assoc (:floating-props use-interactions)
                       :ref #(.setFloating (:refs use-floating) %)
                       :style (:style use-floating))
                [dropdown-component (cond-> opts
                                      dropdown-props (merge dropdown-props))]])]])
         (when (seq @form-errors)
           [form.error/error-label @form-errors])]))))

(defmethod form.form-item/form-item-component :advanced-select
  [opts]
  [:f> advanced-select opts])
